/**
 * Web Push (browser/PWA only): reminders arrive while Nexus is closed. This
 * device's push subscription is stored in the cloud; the send-push function
 * uses it when a reminder from the shared schedule is due (ADR 0011).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { isTauri } from '@nexus/store/tauri';
import { server } from '../sync/config';

// The schema name is configurable per self-hosted project, not the literal "public" type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Client = SupabaseClient<any, string>;

export function pushSupported(): boolean {
  return (
    !isTauri() &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!server().vapidPublicKey
  );
}

/**
 * The active service worker, or undefined when there is none (the dev server
 * has no service worker; only the built app does). Never waits forever.
 */
async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!pushSupported()) return undefined;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return undefined;
  if (reg.active) return reg;
  // Installing right now (first visit): give it a moment to activate.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<undefined>((r) => setTimeout(() => r(undefined), 5_000)),
  ]);
}

/** Can this page subscribe right now (supported and a service worker is active)? */
export async function pushAvailable(): Promise<boolean> {
  return !!(await registration());
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  return (await (await registration())?.pushManager.getSubscription()) ?? null;
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Store (or refresh) this device's subscription in the cloud. */
async function save(sb: Client, sub: PushSubscription, deviceId: string) {
  const json = sub.toJSON();
  const { error } = await sb.from('push_subscriptions').upsert({
    endpoint: sub.endpoint,
    device_id: deviceId,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  });
  if (error) throw new Error(error.message);
}

/** Ask the browser for a push subscription and register it. Needs notification permission. */
export async function enablePush(sb: Client, deviceId: string): Promise<void> {
  const reg = await registration();
  if (!reg) throw new Error('Push is not supported here');
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(server().vapidPublicKey),
    }));
  await save(sb, sub, deviceId);
}

export async function disablePush(sb: Client): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await sb.from('push_subscriptions').delete().eq('endpoint', sub.endpoint);
  await sub.unsubscribe();
}

/** On start: the browser may have rotated the subscription; keep the cloud copy current. */
export async function refreshPush(sb: Client, deviceId: string): Promise<void> {
  const sub = await currentSubscription();
  if (sub) await save(sb, sub, deviceId);
}
